import { ReactNode, useMemo } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Value } from 'baseui/select';
import {
  LoadToEdgeData,
  LoadToVertexData,
  LoadingJobData,
  OneColumnMapping,
  OneColumnMappingDataSourceColumnSource,
  OneColumnMappingMappingWidgetSource,
  SourceType,
} from '@tigergraph/tools-models/loading-job';
import { LuCode, LuFileText } from 'react-icons/lu';
import { SourceSelect } from '@/components/schemaDesigner/attributeEditorContainer/attributeEditor/AttributeSelect';

export interface AttributeSourceSelectProps {
  loadingJob: LoadingJobData | undefined;
  loadingStatement: LoadToVertexData | LoadToEdgeData;
  columnMapping: OneColumnMapping | undefined;
  disabled: boolean;
  onChangeSources: (
    mappingSource: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource
  ) => void;
}

export function AttributeSourceSelect({
  loadingJob,
  loadingStatement,
  columnMapping,
  disabled,
  onChangeSources,
}: AttributeSourceSelectProps) {
  const [css, theme] = useStyletron();
  const sourceOptions = useMemo(() => {
    const options = [] as {
      id: string;
      name: string;
      label: ReactNode;
      mapping: OneColumnMappingDataSourceColumnSource | OneColumnMappingMappingWidgetSource;
    }[];
    if (loadingJob?.dataSet.dataSchema) {
      loadingJob.dataSet.dataSchema.forEach((item, index) => {
        if (item.name) {
          options.push({
            id: `data_source_column_${index}`,
            name: item.name,
            label: (
              <div
                className={css({
                  display: 'flex',
                  columnGap: '4px',
                  alignItems: 'center',
                })}
              >
                <span>
                  <LuFileText size={12} color={theme.colors['dropdown.icon']} />
                </span>
                <span>{item.name}</span>
              </div>
            ),
            mapping: {
              // @ts-ignore
              type: SourceType.DataSourceColumn,
              columnIndex: index,
            },
          });
        }
      });
    }

    return options;
  }, [css, loadingJob?.dataSet.dataSchema, theme.colors]);

  const tokenFunctionOptions = useMemo(() => {
    const options = [...sourceOptions];
    loadingStatement.mappingWidgets.forEach((mappingWidget, index) => {
      options.push({
        id: `token_function_${index}`,
        name: mappingWidget.name ?? `token_function_${index + 1}`,
        label: (
          <div
            className={css({
              display: 'flex',
              columnGap: '4px',
              alignItems: 'center',
            })}
          >
            <span>
              <LuCode size={12} color={theme.colors['dropdown.icon']} />
            </span>
            <span>{mappingWidget.name ?? `token_function_${index + 1}`}</span>
          </div>
        ),
        mapping: {
          // @ts-ignore
          type: SourceType.MappingWidget,
          mappingWidgetIndex: index,
        },
      });
    });

    return options;
  }, [css, theme.colors, loadingStatement.mappingWidgets, sourceOptions]);

  const source = useMemo<Value>(() => {
    // @ts-ignore
    if (columnMapping?.sourceType === SourceType.DataSourceColumn) {
      if (columnMapping?.isSplit) {
        return [{ id: 'token_function_split' }];
      } else {
        return [{ id: `data_source_column_${columnMapping.index}` }];
      }
      // @ts-ignore
    } else if (columnMapping?.sourceType === SourceType.MappingWidget) {
      return [{ id: `token_function_${columnMapping.index}` }];
    } else {
      return [];
    }
  }, [columnMapping]);

  return (
    <SourceSelect
      value={source}
      options={tokenFunctionOptions}
      onChange={({ value }) => {
        if (value.length === 0) {
          onChangeSources({
            // @ts-ignore
            type: SourceType.DataSourceColumn,
            columnIndex: -1,
          });
        } else {
          onChangeSources(value[0].mapping);
        }
      }}
      disabled={disabled}
      placeholder={disabled ? 'Not support' : 'Select source'}
    />
  );
}
